import Button from "@edaz/edazkit/Button/index";
import type { ColumnDefinition } from "@frui.ts/dataviews/dist/dataTypes";
import type { ViewComponent } from "@frui.ts/views";
import { registerView } from "@frui.ts/views";
import type RejectedCustomerRefundCandidateResponse from "entities/rejectedCustomerRefundCandidateResponse";
import RejectedCustomerRefundCandidateStatus, {
  rejectedCustomerRefundCandidateStatusToVariant,
} from "entities/rejectedCustomerRefundCandidateStatus";
import { observer } from "mobx-react-lite";
import React from "react";
import { formatAmount, formatDate, formatLicensePlate, translateEnum } from "utils/localizations";
import RejectedCustomerRefundCandidatesViewModel from "viewModels/finance/customerRefunds/rejectedCustomerRefundCandidates/rejectedCustomerRefundCandidatesViewModel";
import ListView from "views/common/list";
import RejectedCustomerRefundCandidatesFilter from "./rejectedCustomerRefundCandidatesFilter";
import { emptyValue } from "utils/emptyValue";

export const RejectedCustomerRefundCandidatesView: ViewComponent<RejectedCustomerRefundCandidatesViewModel> = observer(
  ({ vm }) => {
    const columns: ColumnDefinition<RejectedCustomerRefundCandidateResponse>[] = [
      {
        title: vm.translateGeneral("licensePlate"),
        valueFormatter: props => formatLicensePlate(props.item.charge.vehicle),
      },
      {
        property: "variableSymbol",
        title: vm.translateAttribute("variableSymbol"),
      },
      {
        title: vm.translateAttribute("originalPaymentAmount"),
        property: "originalPaymentAmount",
        valueFormatter: props => (props.value !== undefined ? formatAmount(props.value) : emptyValue),
      },
      {
        property: "refundPaymentType",
        title: vm.translateAttribute("refundPaymentType"),
        valueFormatter: props => translateEnum("refundPaymentType", props.value),
      },
      {
        property: "officialRecordId",
        title: vm.translateAttribute("officialRecordId"),
      },
      {
        property: "refundAmount",
        title: vm.translateAttribute("refundAmount"),
        valueFormatter: props => formatAmount(props.value),
      },
      {
        property: "rejectionDate",
        title: vm.translateAttribute("rejectionDate"),
        valueFormatter: props => formatDate(props.value),
      },
      {
        property: "status",
        title: vm.translateAttribute("status"),
        valueFormatter: props => {
          const variant = rejectedCustomerRefundCandidateStatusToVariant(props.value);
          return (
            <strong className={`text-${variant}`}>{translateEnum("rejectedCustomerRefundCandidateStatus", props.value)}</strong>
          );
        },
      },
    ];

    const actionButtons = (item: RejectedCustomerRefundCandidateResponse) => {
      const onDetailClick = vm.onItemClick();
      return (
        <>
          {item.status !== RejectedCustomerRefundCandidateStatus.Recreated &&
            item.permittedActions.recreation.isAllowed &&
            vm.allowToRecreate && (
              <Button variant="primary" size="sm" className="rounded" onClick={() => vm.onRecreateClick(item)}>
                {vm.translate("recreate")}
              </Button>
            )}
          <Button variant="success" size="sm" className="rounded" onClick={() => (onDetailClick ? onDetailClick(item) : "")}>
            {vm.translate("showDetail")}
          </Button>
        </>
      );
    };

    return (
      <ListView
        vm={vm}
        columns={columns}
        actionButtons={actionButtons}
        showDetailWithList={true}
        aboveTable={<RejectedCustomerRefundCandidatesFilter vm={vm} />}
      />
    );
  }
);

registerView(RejectedCustomerRefundCandidatesView, RejectedCustomerRefundCandidatesViewModel);
